import { httpClient } from './http';
import { API_ENDPOINTS } from '../config/api';

export interface OrderItem {
  id: string;
  menuItemId: string;
  menuItemName: string;
  quantity: number;
  price: number | string;
  notes?: string;
  status: string;
  orderId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Order {
  id: string;
  waiterId?: string | null;
  comensalId?: string | null;
  restaurantId: string;
  restaurantName: string;
  tableId?: string;
  tableNumber?: number;
  totalAmount: string | number;
  status: 'created' | 'pending' | 'preparing' | 'ready' | 'served' | 'cancelled';
  paymentMethod?: string;
  deliveryAddress?: string | null;
  notes?: string;
  rating?: number | null;
  review?: string | null;
  estimatedDelivery?: string | null;
  actualDelivery?: string | null;
  isPaid?: boolean;
  createdAt: string;
  updatedAt: string;
  userName?: string;
  items: OrderItem[];
  assignedWaiters?: Array<{id: string; name: string}>; // Array de meseros asignados a la mesa
}

export interface OrdersResponse {
  data: Order[];
  pagination: {
    page: number;
    size: number;
    total: number;
    pages: number;
  };
}

export interface OrderFilters {
  restaurantId: string; // Obligatorio
  status?: string;
  tableNumber?: number;
  tableId?: string; // ID de la mesa para filtrar por mesa específica
  userId?: string;
}

export interface OrderPagination {
  page: number;
  size: number;
}

/**
 * Obtener órdenes con filtros y paginación
 */
export const getOrders = async (
  userId: string,
  filters: OrderFilters,
  pagination: OrderPagination = { page: 1, size: 50 }
): Promise<OrdersResponse> => {
  if (!filters.restaurantId) {
    throw new Error('restaurantId is required');
  }

  const params = new URLSearchParams();
  
  // Agregar filtros (restaurantId ya está en la URL)
  if (filters.status) {
    params.append('status', filters.status);
  }
  if (filters.tableNumber) {
    params.append('tableNumber', filters.tableNumber.toString());
  }
  if (filters.tableId) {
    params.append('tableId', filters.tableId);
  }
  if (filters.userId) {
    params.append('userId', filters.userId);
  }
  
  // Agregar paginación
  params.append('page', pagination.page.toString());
  params.append('size', pagination.size.toString());
  
  const endpoint = API_ENDPOINTS.ORDERS(userId, filters.restaurantId);
  const response = await httpClient.get<any>(`${endpoint}?${params.toString()}`, undefined, 'authenticated');
  return response;
};

/**
 * Obtener cola pública combinada (pending + in progress) para un restaurante
 */
export const getPublicOrderQueue = async (
  restaurantId: string
): Promise<any> => {
  const endpoint = `${API_ENDPOINTS.PUBLIC_ORDERS}?restaurantId=${encodeURIComponent(restaurantId)}`;
  const response = await httpClient.get<any>(endpoint, undefined, 'public');
  return response;
};

/**
 * Obtener órdenes por restaurante
 */
export const getOrdersByRestaurant = async (
  userId: string,
  restaurantId: string,
  status?: string
): Promise<Order[]> => {
  if (!restaurantId) {
    throw new Error('restaurantId is required');
  }

  const filters: OrderFilters = { restaurantId: restaurantId };
  if (status) {
    filters.status = status;
  }
  
  const response = await getOrders(userId, filters, { page: 1, size: 100 });
  return response.data;
};

/**
 * Obtener órdenes por estado específico
 */
export const getOrdersByStatus = async (
  userId: string,
  restaurantId: string,
  status: string
): Promise<Order[]> => {
  return getOrdersByRestaurant(userId, restaurantId, status);
};

/**
 * Obtener órdenes por mesa específica (para meseros asignados a esa mesa)
 */
export const getOrdersByTable = async (
  userId: string,
  restaurantId: string,
  tableId: string,
  status?: string
): Promise<Order[]> => {
  const filters: OrderFilters = { 
    restaurantId: restaurantId,
    tableId: tableId
  };
  
  if (status) {
    filters.status = status;
  }
  
  const response = await getOrders(userId, filters, { page: 1, size: 100 });
  return response.data;
};

/**
 * Obtener órdenes de todas las mesas asignadas a un mesero
 * Nota: Esta función requiere que el backend soporte el filtro por tableId
 */
export const getOrdersByAssignedTables = async (
  userId: string,
  restaurantId: string,
  status?: string
): Promise<Order[]> => {
  // Por ahora, obtener todas las órdenes del restaurante
  // El backend filtrará automáticamente por las mesas asignadas al mesero
  const filters: OrderFilters = { 
    restaurantId: restaurantId
  };
  
  if (status) {
    filters.status = status;
  }
  
  const response = await getOrders(userId, filters, { page: 1, size: 100 });
  return response.data;
};

/**
 * Obtener una orden específica por ID
 */
export const getOrderById = async (userId: string, restaurantId: string, orderId: string): Promise<Order> => {
  const endpoint = API_ENDPOINTS.ORDER_BY_ID(userId, restaurantId, orderId);
  const response = await httpClient.get<any>(endpoint, undefined, 'authenticated');
  return response;
};

/**
 * Actualizar el estado de una orden
 */
export const updateOrderStatus = async (
  userId: string,
  restaurantId: string,
  orderId: string,
  status: Order['status']
): Promise<Order> => {
  const endpoint = API_ENDPOINTS.ORDER_STATUS(userId, restaurantId, orderId);
  const response = await httpClient.put<any>(endpoint, { status }, 'authenticated');
  return response;
};

/**
 * Cancelar una orden
 */
export const cancelOrder = async (userId: string, restaurantId: string, orderId: string): Promise<Order> => {
  const endpoint = API_ENDPOINTS.ORDER_BY_ID(userId, restaurantId, orderId);
  const response = await httpClient.delete<any>(endpoint, 'authenticated');
  return response;
};

/**
 * Crear una nueva orden (endpoint público)
 */
export const createOrder = async (
  orderData: {
    restaurant_id: string;
    table_id?: string;
    items: Array<{
      menu_item_id: string;
      quantity: number;
      notes?: string;
    }>;
    payment_method?: string;
    delivery_address?: string;
    notes?: string;
  }
): Promise<Order> => {
  const endpoint = API_ENDPOINTS.PUBLIC_ORDERS;
  const response = await httpClient.post<any>(endpoint, orderData, 'public');
  return response;
}; 